import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import React, { useEffect, useRef, useState } from "react";
import { createOptions } from "./config";
import { TemperatureCharProps } from "./TemperatureChartProps";
import { thorTemperatureChartStyles } from "./ThorTemperatureChartStyles";
import { Chart, ChartDataset } from "chart.js";
import { Button, Collapse, Grid, Typography } from "@material-ui/core";
import moment from "moment";
import DeviceDetails from "components/DeviceDetails/DeviceDetails";
import { DeviceDetailsInfo } from "components/DeviceDetails/DeviceDetailsInfo";
import { observer } from "mobx-react";
import { useStore } from "store/StoreConfigs";
import clsx from "clsx";
import { ReactComponent as EventChevronDown } from "icons/EventChevronDown.svg";
import { ReactComponent as EventChevronUp } from "icons/EventChevronUp.svg";

export default observer(function ThorTemperatureChart({
    temperatureItems,
    isLoading,
    temperatureUpperLimit,
    temperatureLowerLimit,
}: TemperatureCharProps) {
    const classes = thorTemperatureChartStyles();

    const { thorEventViewStore } = useStore();

    const [deviceDetails, setDeviceDetails] = useState<DeviceDetailsInfo>();
    const [isCollapsed, setIsCollapsed] = useState(false);

    if (!temperatureItems) {
        return <div>No data</div>;
    }

    const allDates = [] as Array<number>;

    temperatureItems.forEach((element) => {
        allDates.concat(element.data.map((v) => v.x.valueOf()));
    });

    const minDate = Math.max(...allDates);
    const maxDate = Math.max(...allDates);
    const divRef = useRef<HTMLCanvasElement | null>(null);
    const [chart, setChart] = useState<Chart>();

    const datasets: ChartDataset<"line">[] = temperatureItems.map((item) => ({
        label: item.label,
        data: item.data.map((xy) => ({ x: moment.utc(xy.x).valueOf(), y: xy.y })),
        borderColor: item.backgroundColor,
        backgroundColor: item.backgroundColor,
        borderWidth: item.borderWidth,
    }));

    useEffect(() => {
        if (divRef.current === null) {
            return;
        }

        const chartInstance = new Chart(divRef.current, {
            type: "line",
            data: {
                datasets,
            },
            options: createOptions(minDate, maxDate, temperatureLowerLimit, temperatureUpperLimit),
        });
        chartInstance.update();
        setChart(chartInstance);

        return () => chartInstance.destroy();
    }, [temperatureItems]);

    return (
        <div className={classes.main}>
            <Grid container justifyContent="center">
                <Grid item xs={11} style={{ marginTop: "20px" }}>
                    <div className={classes.container}>
                        {isLoading ? (
                            <LoadingIndicator className={classes.loadingIndicator} />
                        ) : (
                            <>
                                <Button
                                    onClick={() => {
                                        chart?.resetZoom();
                                    }}
                                    className={classes.resetBtn}
                                >
                                    <Typography>Reset zoom</Typography>
                                </Button>
                                <canvas ref={divRef} />
                            </>
                        )}
                    </div>
                </Grid>
            </Grid>
            <Grid container>
                {temperatureItems.map((x) => (
                    <Grid xs={3} item className={classes.devicesInfoRow} key={x.label}>
                        <Typography variant="h5">{x.label}</Typography>

                        <Button
                            className={clsx(classes.button, {
                                [classes.selected]: thorEventViewStore.selectedDevise === x.label,
                                [classes.unSelected]: thorEventViewStore.selectedDevise !== x.label,
                            })}
                            onClick={
                                isCollapsed && thorEventViewStore.selectedDevise === x.label
                                    ? () => {
                                          setIsCollapsed(false);
                                          thorEventViewStore.setSelectedDevise("");
                                      }
                                    : () => {
                                          setDeviceDetails({
                                              configuration: x.configuration,
                                              readInformation: x.readInformation,
                                              recordingInformation: x.recordingInformation,
                                              summaryData: x.summaryData,
                                          });
                                          setIsCollapsed(true);
                                          thorEventViewStore.setSelectedDevise(x.label);
                                      }
                            }
                        >
                            {isCollapsed && thorEventViewStore.selectedDevise === x.label ? (
                                <Typography className={classes.icon}>
                                    Hide Monitor Details
                                    <EventChevronUp style={{ fill: "white" }} />
                                </Typography>
                            ) : (
                                <Typography className={classes.icon}>
                                    View Monitor Details
                                    <EventChevronDown style={{ fill: "#270ADC" }} />
                                </Typography>
                            )}
                        </Button>
                    </Grid>
                ))}
            </Grid>
            <Collapse in={isCollapsed}>{deviceDetails && <DeviceDetails {...deviceDetails} />}</Collapse>
        </div>
    );
});
